import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
  Chip,
  Box,
  Tabs,
  Tab,
  Rating,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Chair as ChairIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Report as ReportIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import axios from "axios";

const statusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "approved":
    case "resolved":
      return "success";
    case "pending":
      return "warning";
    case "cancelled":
    case "rejected":
      return "error";
    default:
      return "default";
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StudentBookingHistory = ({ open, onClose, studentId }) => {
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [tab, setTab] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  useEffect(() => {
    if (open && studentId) {
      fetchProfile();
    }
    // eslint-disable-next-line
  }, [open, studentId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}admin/students/${studentId}/full-profile/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProfileData(res.data);
    } catch (err) {
      toast.error("Failed to fetch student profile");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    const toastId = toast.loading("Downloading profile PDF...");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}admin/students/${studentId}/full-profile/pdf/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `student-profile-${studentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Profile PDF downloaded", { id: toastId });
    } catch (err) {
      toast.error("Failed to download PDF", { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const profile = profileData?.profile;
  const libraryBookings = profileData?.library_bookings || [];
  const complaints = profileData?.complaints || [];
  const reviews = profileData?.reviews || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: "95vh",
          width: "95vw",
          maxWidth: "1000px",
          margin: "2.5vh auto",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <DialogTitle className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PersonIcon className="text-white" />
            <Typography variant="h6" className="font-bold text-base">
              Student Full Profile
            </Typography>
          </div>
          <IconButton onClick={onClose} sx={{ color: "white" }} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="p-4 sm:p-6 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <CircularProgress size={60} className="text-blue-600 mb-4" />
              <Typography variant="body1" className="text-gray-600 font-medium">
                Loading student profile...
              </Typography>
            </div>
          </div>
        ) : profile ? (
          <div className="space-y-6">
            {/* Profile Section */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 mt-2">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <Avatar
                  src={
                    profile.profile_photo ||
                    `https://ui-avatars.com/api/?name=${profile.first_name}+${profile.last_name}`
                  }
                  alt={`${profile.first_name} ${profile.last_name}`}
                  sx={{
                    width: { xs: 100, sm: 140 },
                    height: { xs: 100, sm: 140 },
                    cursor: profile.profile_photo ? "pointer" : "default",
                    border: "4px solid #e5e7eb",
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  }}
                  className="hover:scale-105 transition-transform duration-200"
                  onClick={() => {
                    if (profile.profile_photo)
                      setPreviewImg(profile.profile_photo);
                  }}
                />
                <div className="text-center sm:text-left flex-1">
                  <Typography
                    variant="h4"
                    className="font-bold text-gray-800 text-xl sm:text-2xl mb-2"
                  >
                    {profile.first_name} {profile.middle_name}{" "}
                    {profile.last_name}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="text-gray-500 text-sm mb-3"
                  >
                    @{profile.username}
                  </Typography>
                  <Chip
                    label={profile.role}
                    color="primary"
                    size="medium"
                    className="font-semibold"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadPDF}
                    disabled={downloading}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                    size="medium"
                  >
                    {downloading ? "Downloading..." : "Download PDF"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Contact & Info */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <Typography
                variant="h6"
                className="font-bold text-gray-800 mb-4 flex items-center gap-2"
              >
                <SchoolIcon className="text-blue-600" />
                Contact & Personal Information
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <Typography
                      variant="subtitle2"
                      className="flex items-center gap-2 text-gray-600 font-semibold mb-2"
                    >
                      <PhoneIcon className="text-blue-600" /> Phone Number
                    </Typography>
                    <Typography
                      variant="body1"
                      className="font-medium text-gray-900 text-lg"
                    >
                      {profile.phone_number}
                    </Typography>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <Typography
                      variant="subtitle2"
                      className="flex items-center gap-2 text-gray-600 font-semibold mb-2"
                    >
                      <LocationIcon className="text-green-600" /> Address
                    </Typography>
                    <Typography
                      variant="body1"
                      className="font-medium text-gray-900"
                    >
                      {profile.address}
                    </Typography>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <Typography
                      variant="subtitle2"
                      className="flex items-center gap-2 text-gray-600 font-semibold mb-2"
                    >
                      <SchoolIcon className="text-purple-600" /> Education
                    </Typography>
                    <Typography
                      variant="body1"
                      className="font-medium text-gray-900 text-lg"
                    >
                      {profile.education}
                    </Typography>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <Typography
                      variant="subtitle2"
                      className="flex items-center gap-2 text-gray-600 font-semibold mb-2"
                    >
                      Email Address
                    </Typography>
                    <Typography
                      variant="body1"
                      className="font-medium text-gray-900"
                    >
                      {profile.email}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs for Bookings, Complaints, Reviews */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  backgroundColor: "#f8fafc",
                }}
              >
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    "& .MuiTab-root": {
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      textTransform: "none",
                      minHeight: "56px",
                      color: "#64748b",
                      "&.Mui-selected": {
                        color: "#2563eb",
                      },
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#2563eb",
                      height: 3,
                    },
                  }}
                >
                  <Tab
                    label={`Library Bookings (${libraryBookings.length})`}
                    icon={<ChairIcon />}
                    iconPosition="start"
                  />
                  <Tab
                    label={`Complaints (${complaints.length})`}
                    icon={<ReportIcon />}
                    iconPosition="start"
                  />
                  <Tab
                    label={`Reviews (${reviews.length})`}
                    icon={<StarIcon />}
                    iconPosition="start"
                  />
                </Tabs>
              </Box>
              <Box sx={{ p: 2 }}>
                {tab === 0 && (
                  <div className="space-y-4 max-h-80 overflow-y-auto">
                    {libraryBookings.length > 0 ? (
                      libraryBookings.map((b) => (
                        <div
                          key={b.id}
                          className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200 hover:shadow-md transition-shadow duration-200"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-green-600 p-2 rounded-lg">
                              <ChairIcon className="text-white text-lg" />
                            </div>
                            <div className="flex-1">
                              <span className="font-bold text-gray-900 text-base">
                                Seat {b.seat}
                              </span>
                            </div>
                            <Chip
                              label={b.status}
                              color={statusColor(b.status)}
                              size="medium"
                              className="font-semibold capitalize"
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div className="bg-white rounded-lg p-3 border border-green-100">
                              <div className="text-gray-600 font-medium mb-1">
                                Start Date
                              </div>
                              <div className="font-semibold text-gray-900">
                                {formatDate(b.start_date)}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-green-100">
                              <div className="text-gray-600 font-medium mb-1">
                                Booked On
                              </div>
                              <div className="font-semibold text-gray-900">
                                {b.booking_date}
                              </div>
                            </div>
                          </div>
                          {b.seat_description && (
                            <div className="mt-3 bg-white rounded-lg p-3 border border-green-100">
                              <div className="text-gray-600 font-medium mb-1">
                                Seat Description
                              </div>
                              <div className="font-semibold text-gray-900">
                                {b.seat_description}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <ChairIcon className="text-gray-400 text-6xl mb-4" />
                        <Typography className="text-gray-500 text-lg font-medium">
                          No library bookings found.
                        </Typography>
                      </div>
                    )}
                  </div>
                )}
                {tab === 1 && (
                  <div className="space-y-4 max-h-80 overflow-y-auto">
                    {complaints.length > 0 ? (
                      complaints.map((c) => (
                        <div
                          key={c.id}
                          className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-4 border border-red-200 hover:shadow-md transition-shadow duration-200"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-red-600 p-2 rounded-lg">
                              <ReportIcon className="text-white text-lg" />
                            </div>
                            <div className="flex-1">
                              <span className="font-bold text-gray-900 text-base">
                                {c.title}
                              </span>
                            </div>
                            <Chip
                              label={c.status}
                              color={statusColor(c.status)}
                              size="medium"
                              className="font-semibold capitalize"
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
                            <div className="bg-white rounded-lg p-3 border border-red-100">
                              <div className="text-gray-600 font-medium mb-1">
                                Category
                              </div>
                              <div className="font-semibold text-gray-900 capitalize">
                                {c.category}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-red-100">
                              <div className="text-gray-600 font-medium mb-1">
                                Submitted
                              </div>
                              <div className="font-semibold text-gray-900">
                                {formatDate(c.submitted_on)}
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-red-100 mb-3">
                            <div className="text-gray-600 font-medium mb-1">
                              Description
                            </div>
                            <div className="font-semibold text-gray-900">
                              {c.description}
                            </div>
                          </div>
                          {c.screenshot && (
                            <div className="bg-white rounded-lg p-3 border border-red-100">
                              <Tooltip title="View Screenshot">
                                <span
                                  className="text-blue-600 hover:text-blue-800 font-semibold text-sm cursor-pointer inline-flex items-center gap-1 transition-colors duration-200"
                                  onClick={() => {
                                    const url = c.screenshot.startsWith("http")
                                      ? c.screenshot
                                      : `${process.env.REACT_APP_API_URL?.replace(
                                          "/api/",
                                          ""
                                        )}${c.screenshot}`;
                                    setPreviewImg(url);
                                  }}
                                >
                                  📷 View Screenshot
                                </span>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <ReportIcon className="text-gray-400 text-6xl mb-4" />
                        <Typography className="text-gray-500 text-lg font-medium">
                          No complaints found.
                        </Typography>
                      </div>
                    )}
                  </div>
                )}
                {tab === 2 && (
                  <div className="space-y-4 max-h-80 overflow-y-auto">
                    {reviews.length > 0 ? (
                      reviews.map((r) => (
                        <div
                          key={r.id}
                          className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-200 hover:shadow-md transition-shadow duration-200"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="bg-yellow-600 p-2 rounded-lg">
                              <StarIcon className="text-white text-lg" />
                            </div>
                            <div className="flex-1">
                              <span className="font-bold text-gray-900 text-base">
                                {r.title}
                              </span>
                            </div>
                            <Chip
                              label={r.is_approved ? "Approved" : "Pending"}
                              color={r.is_approved ? "success" : "warning"}
                              size="medium"
                              className="font-semibold capitalize"
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-3">
                            <div className="bg-white rounded-lg p-3 border border-yellow-100">
                              <div className="text-gray-600 font-medium mb-1">
                                By
                              </div>
                              <div className="font-semibold text-gray-900">
                                {r.name}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-yellow-100">
                              <div className="text-gray-600 font-medium mb-1">
                                Submitted
                              </div>
                              <div className="font-semibold text-gray-900">
                                {formatDate(r.submitted_on)}
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-yellow-100 mb-3">
                            <div className="text-gray-600 font-medium mb-1">
                              Description
                            </div>
                            <div className="font-semibold text-gray-900">
                              {r.description}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-yellow-100">
                            <div className="text-gray-600 font-medium mb-2">
                              Rating
                            </div>
                            <Rating value={r.rating} readOnly size="large" />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <StarIcon className="text-gray-400 text-6xl mb-4" />
                        <Typography className="text-gray-500 text-lg font-medium">
                          No reviews found.
                        </Typography>
                      </div>
                    )}
                  </div>
                )}
              </Box>
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <PersonIcon className="text-gray-400 text-6xl mb-4" />
            <Typography
              variant="body1"
              className="text-gray-500 text-lg font-medium"
            >
              No student data available
            </Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions className="p-4 sm:p-6 pt-0">
        <Button
          onClick={onClose}
          variant="contained"
          className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-8 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
          fullWidth={window.innerWidth < 640}
        >
          Close
        </Button>
      </DialogActions>
      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImg}
        onClose={() => setPreviewImg(null)}
        maxWidth="md"
      >
        <Box className="flex overflow-hidden flex-col items-center justify-center p-4">
          <Box
            sx={{
              width: "80vw",
              height: "70vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f9f9f9",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <img
              src={previewImg}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
                margin: "auto",
              }}
            />
          </Box>
          <Button
            onClick={() => setPreviewImg(null)}
            sx={{ mt: 2 }}
            variant="contained"
          >
            Close
          </Button>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default StudentBookingHistory;
